import { useEffect, useMemo, useState } from "react"
import { ArrowLeft, Plus } from "lucide-react"
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { BUDGET_SCOPE } from "@/lib/constants"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useCategories,
  useBudgets,
  useReorderCategories,
  hasSubcategoryBudgets,
  hasParentBudget,
} from '@/lib/hooks'
import { useAuth } from '@/lib/auth-context'
import type { Category, Budget } from '@/lib/hooks'
import { formatAmount } from '@/lib/utils'
import { AddCategorySheet } from '@/components/category/add-category-sheet'
import { DeleteCategoryDialog } from '@/components/category/delete-category-dialog'
import { ParentCategoryEditSheet } from '@/components/category/parent-category-edit-sheet'
import { SubcategoryEditSheet } from '@/components/category/subcategory-edit-sheet'
import { Subcategory } from '@/components/category/subcategory'
import { DEFAULT_CATEGORY_COLOR } from '@/lib/constants'
import { mergePreservingOrder } from '@/lib/sortable-reorder'

type ParentRowData = {
  parent: Category
  hDisplayAmount: number | undefined
  pDisplayAmount: number | undefined
  hHasSubBudgets: boolean
  pHasSubBudgets: boolean
}

function ParentRowContent({
  parent,
  hDisplayAmount,
  pDisplayAmount,
  hHasSubBudgets,
  pHasSubBudgets,
}: ParentRowData) {
  return (
    <>
      <div className="flex items-center gap-2">
        <div
          className="h-4 w-4 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: parent.color ?? DEFAULT_CATEGORY_COLOR }}
        />
        <h2 className="text-sm font-semibold text-foreground">{parent.name}</h2>
      </div>
      {!parent.is_hidden && (hDisplayAmount != null || pDisplayAmount != null) && (
        <div className="shrink-0 text-right text-xs text-muted-foreground">
          {hDisplayAmount != null && (
            <span className="inline-flex items-center gap-0.5 text-foreground">
              <BUDGET_SCOPE.household.Icon className="h-3 w-3 text-muted-foreground" />
              {formatAmount(hDisplayAmount)}
              {hHasSubBudgets && <span className="text-muted-foreground"> (計)</span>}
            </span>
          )}
          {hDisplayAmount != null && pDisplayAmount != null && (
            <span className="mx-1">·</span>
          )}
          {pDisplayAmount != null && (
            <span className="inline-flex items-center gap-0.5 text-foreground">
              <BUDGET_SCOPE.personal.Icon className="h-3 w-3 text-muted-foreground" />
              {formatAmount(pDisplayAmount)}
              {pHasSubBudgets && <span className="text-muted-foreground"> (計)</span>}
            </span>
          )}
        </div>
      )}
    </>
  )
}

function SortableParentRow({
  data,
  onTap,
  draggable,
}: {
  data: ParentRowData
  onTap: () => void
  draggable: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: data.parent.id,
    disabled: !draggable,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className={`flex items-center justify-between rounded-md px-3 py-2 active:bg-accent/80 cursor-pointer transition-colors ${data.parent.is_hidden ? '' : 'mb-1 border-b'}`}
        onClick={onTap}
      >
        <ParentRowContent {...data} />
      </div>
    </div>
  )
}

function SortableSubcategoryRow({
  category,
  onTap,
  householdBudget,
  personalBudget,
}: {
  category: Category
  onTap: (c: Category) => void
  householdBudget?: Budget
  personalBudget?: Budget
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Subcategory
        category={category}
        onTap={onTap}
        householdBudget={householdBudget}
        personalBudget={personalBudget}
      />
    </div>
  )
}

function ParentDragPreview({ data }: { data: ParentRowData }) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2 shadow-lg">
      <ParentRowContent {...data} />
    </div>
  )
}

function SubcategoryDragPreview({
  category,
  householdBudget,
  personalBudget,
}: {
  category: Category
  householdBudget?: Budget
  personalBudget?: Budget
}) {
  return (
    <div className="rounded-md border bg-card shadow-lg">
      <Subcategory
        category={category}
        onTap={() => {}}
        householdBudget={householdBudget}
        personalBudget={personalBudget}
      />
    </div>
  )
}

export default function CategoriesPage() {
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { user: currentUser } = useAuth()
  const { data: householdBudgets = [], isLoading: hbLoading } = useBudgets()
  const { data: personalBudgets = [], isLoading: pbLoading } = useBudgets(currentUser?.id)
  const reorderMutation = useReorderCategories()

  const [order, setOrder] = useState<Category[]>([])

  useEffect(() => {
    if (!categoriesData) return
    setOrder((prev) => mergePreservingOrder(prev, categoriesData))
  }, [categoriesData])

  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addParentId, setAddParentId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null)
  const [subSheetOpen, setSubSheetOpen] = useState(false)
  const [subSheetTarget, setSubSheetTarget] = useState<Category | null>(null)
  const [parentSheetOpen, setParentSheetOpen] = useState(false)
  const [parentSheetTarget, setParentSheetTarget] = useState<Category | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  )

  const allParents = useMemo(
    () =>
      order
        .filter((c) => c.parent_id === null)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [order],
  )
  const visibleParents = useMemo(() => allParents.filter((p) => !p.is_hidden), [allParents])
  const hiddenParents = useMemo(() => allParents.filter((p) => p.is_hidden), [allParents])
  const visibleParentIds = useMemo(() => visibleParents.map((p) => p.id), [visibleParents])
  const hiddenParentIds = useMemo(() => hiddenParents.map((p) => p.id), [hiddenParents])

  const childrenByParent = useMemo(() => {
    const map: Record<string, Category[]> = {}
    for (const c of order) {
      if (!c.parent_id) continue
      if (!map[c.parent_id]) map[c.parent_id] = []
      map[c.parent_id].push(c)
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    }
    return map
  }, [order])

  const getHouseholdBudget = (categoryId: string) =>
    householdBudgets.find((b) => b.category_id === categoryId)
  const getPersonalBudget = (categoryId: string) =>
    personalBudgets.find((b) => b.category_id === categoryId)

  const getSubBudgetSum = (parentId: string, budgets: Budget[]) => {
    const childIds = new Set(
      order.filter((c) => c.parent_id === parentId).map((c) => c.id),
    )
    return budgets
      .filter((b) => childIds.has(b.category_id))
      .reduce((sum, b) => sum + b.amount, 0)
  }

  const parentRowData = (parent: Category): ParentRowData => {
    const hBudget = getHouseholdBudget(parent.id)
    const pBudget = getPersonalBudget(parent.id)
    const hHasSubBudgets = hasSubcategoryBudgets(parent.id, householdBudgets, order)
    const pHasSubBudgets = hasSubcategoryBudgets(parent.id, personalBudgets, order)
    return {
      parent,
      hHasSubBudgets,
      pHasSubBudgets,
      hDisplayAmount: hHasSubBudgets ? getSubBudgetSum(parent.id, householdBudgets) : hBudget?.amount,
      pDisplayAmount: pHasSubBudgets ? getSubBudgetSum(parent.id, personalBudgets) : pBudget?.amount,
    }
  }

  const loading = categoriesLoading || hbLoading || pbLoading

  if (loading && order.length === 0) {
    return (
      <div className="bg-background">
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="mb-6 flex items-center gap-3">
            <Link to="/settings" replace>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="size-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">カテゴリーと予算</h1>
          </div>
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <div className="mb-1 flex items-center justify-between rounded-md border-b px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-3 w-24" />
                </div>
                <div className="space-y-1">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div key={j} className="flex items-center justify-between px-3 py-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const handleDeleteRequest = (cat: Category) => {
    setDeleteTarget(cat)
    setDeleteDialogOpen(true)
  }

  const findContext = (id: string): { kind: 'parent' } | { kind: 'children'; parentId: string } | null => {
    if (allParents.some((p) => p.id === id)) return { kind: 'parent' }
    const cat = order.find((c) => c.id === id)
    if (cat?.parent_id) return { kind: 'children', parentId: cat.parent_id }
    return null
  }

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingId(String(event.active.id))
  }
  const handleDragCancel = () => setDraggingId(null)

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const activeId = String(active.id)
    const overId = String(over.id)
    const activeCtx = findContext(activeId)
    const overCtx = findContext(overId)
    if (!activeCtx || !overCtx) return
    if (activeCtx.kind !== overCtx.kind) return

    if (activeCtx.kind === 'parent') {
      // visible / hidden の中で同じ群同士のみ並び替え
      const activeHidden = order.find((c) => c.id === activeId)?.is_hidden ?? false
      const overHidden = order.find((c) => c.id === overId)?.is_hidden ?? false
      if (activeHidden !== overHidden) return
      const list = activeHidden ? hiddenParents : visibleParents
      const oldIndex = list.findIndex((c) => c.id === activeId)
      const newIndex = list.findIndex((c) => c.id === overId)
      if (oldIndex < 0 || newIndex < 0) return
      const reordered = arrayMove(list, oldIndex, newIndex)
      const merged = activeHidden
        ? [...visibleParents, ...reordered]
        : [...reordered, ...hiddenParents]
      const renumbered = merged.map((c, idx) => ({ ...c, sort_order: idx }))

      const byId = new Map(renumbered.map((c) => [c.id, c]))
      setOrder((prev) => prev.map((c) => byId.get(c.id) ?? c))
      reorderMutation.mutate(renumbered.map((c) => ({ id: c.id, sort_order: c.sort_order ?? 0 })))
    } else {
      const parentId = activeCtx.parentId
      if (overCtx.kind !== 'children' || overCtx.parentId !== parentId) return
      const siblings = childrenByParent[parentId] ?? []
      const oldIndex = siblings.findIndex((c) => c.id === activeId)
      const newIndex = siblings.findIndex((c) => c.id === overId)
      if (oldIndex < 0 || newIndex < 0) return
      const reordered = arrayMove(siblings, oldIndex, newIndex).map((c, idx) => ({
        ...c,
        sort_order: idx,
      }))
      const byId = new Map(reordered.map((c) => [c.id, c]))
      setOrder((prev) => prev.map((c) => byId.get(c.id) ?? c))
      reorderMutation.mutate(reordered.map((c) => ({ id: c.id, sort_order: c.sort_order ?? 0 })))
    }
  }

  const draggingCategory = draggingId ? order.find((c) => c.id === draggingId) ?? null : null
  const draggingIsParent = draggingCategory ? draggingCategory.parent_id === null : false

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-lg px-4 py-6">
        <div className="mb-4 flex items-center gap-3">
          <Link to="/settings" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">カテゴリーと予算</h1>
        </div>

        <p className="mb-4 text-sm text-muted-foreground">
          長押しで並び替えできます。
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="space-y-6">
            <SortableContext items={visibleParentIds} strategy={verticalListSortingStrategy}>
              {visibleParents.map((parent) => {
                const data = parentRowData(parent)
                const children = childrenByParent[parent.id] ?? []
                return (
                  <div key={parent.id}>
                    <SortableParentRow
                      data={data}
                      draggable
                      onTap={() => {
                        setParentSheetTarget(parent)
                        setParentSheetOpen(true)
                      }}
                    />
                    <SortableContext
                      items={children.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {children.map((child) => (
                          <SortableSubcategoryRow
                            key={child.id}
                            category={child}
                            onTap={(c) => {
                              setSubSheetTarget(c)
                              setSubSheetOpen(true)
                            }}
                            householdBudget={getHouseholdBudget(child.id)}
                            personalBudget={getPersonalBudget(child.id)}
                          />
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-muted-foreground"
                          onClick={() => {
                            setAddParentId(parent.id)
                            setAddDialogOpen(true)
                          }}
                        >
                          <Plus className="size-4" />
                          カテゴリを追加
                        </Button>
                      </div>
                    </SortableContext>
                  </div>
                )
              })}
            </SortableContext>

            {hiddenParents.length > 0 && (
              <>
                <div className="flex items-center gap-3 pb-2 pt-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">非表示</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <SortableContext items={hiddenParentIds} strategy={verticalListSortingStrategy}>
                  {hiddenParents.map((parent) => {
                    const data = parentRowData(parent)
                    return (
                      <div key={parent.id} className="opacity-50">
                        <SortableParentRow
                          data={data}
                          draggable={false}
                          onTap={() => {
                            setParentSheetTarget(parent)
                            setParentSheetOpen(true)
                          }}
                        />
                      </div>
                    )
                  })}
                </SortableContext>
              </>
            )}
          </div>

          <DragOverlay dropAnimation={null}>
            {draggingCategory ? (
              draggingIsParent ? (
                <ParentDragPreview data={parentRowData(draggingCategory)} />
              ) : (
                <SubcategoryDragPreview
                  category={draggingCategory}
                  householdBudget={getHouseholdBudget(draggingCategory.id)}
                  personalBudget={getPersonalBudget(draggingCategory.id)}
                />
              )
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <AddCategorySheet
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        parentId={addParentId}
        userId={currentUser?.id}
        allowPersonal
      />
      <DeleteCategoryDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        category={deleteTarget}
      />
      <ParentCategoryEditSheet
        open={parentSheetOpen}
        onOpenChange={setParentSheetOpen}
        category={parentSheetTarget}
        householdBudget={parentSheetTarget ? getHouseholdBudget(parentSheetTarget.id) : undefined}
        personalBudget={parentSheetTarget ? getPersonalBudget(parentSheetTarget.id) : undefined}
        userId={currentUser?.id ?? ''}
        onDelete={handleDeleteRequest}
        householdLocked={parentSheetTarget ? hasSubcategoryBudgets(parentSheetTarget.id, householdBudgets, order) : false}
        personalLocked={parentSheetTarget ? hasSubcategoryBudgets(parentSheetTarget.id, personalBudgets, order) : false}
        householdLockedAmount={parentSheetTarget ? getSubBudgetSum(parentSheetTarget.id, householdBudgets) : 0}
        personalLockedAmount={parentSheetTarget ? getSubBudgetSum(parentSheetTarget.id, personalBudgets) : 0}
      />
      <SubcategoryEditSheet
        open={subSheetOpen}
        onOpenChange={setSubSheetOpen}
        category={subSheetTarget}
        parentCategories={visibleParents.concat(hiddenParents)}
        onDelete={handleDeleteRequest}
        householdBudget={subSheetTarget ? getHouseholdBudget(subSheetTarget.id) : undefined}
        personalBudget={subSheetTarget ? getPersonalBudget(subSheetTarget.id) : undefined}
        parentHasHouseholdBudget={subSheetTarget?.parent_id ? hasParentBudget(subSheetTarget.parent_id, householdBudgets) : false}
        parentHasPersonalBudget={subSheetTarget?.parent_id ? hasParentBudget(subSheetTarget.parent_id, personalBudgets) : false}
        userId={currentUser?.id ?? ''}
      />
    </div>
  )
}
